import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { StaffMember as ImportedStaffMember } from '../../services/staffManagementService';

// Extended interface for org chart with hierarchy fields
interface StaffMemberWithHierarchy {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  position: string;
  department: string;
  photo_url?: string;
  manager_id?: string;
  reports?: StaffMemberWithHierarchy[];
}

interface OrgChartProps {
  staffMembers: ImportedStaffMember[];
}

const OrgChart: React.FC<OrgChartProps> = ({ staffMembers }) => {
  const [viewType, setViewType] = useState<'hierarchy' | 'department' | 'list'>('hierarchy');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [orgStructure, setOrgStructure] = useState<StaffMemberWithHierarchy[]>([]);

  useEffect(() => {
    buildOrgStructure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffMembers]);

  const buildOrgStructure = () => {
    // Build hierarchical structure based on manager_id
    // Note: manager_id would need to be added to the StaffMember database table
    const staffMap = new Map<string, StaffMemberWithHierarchy>();
    const rootStaff: StaffMemberWithHierarchy[] = [];

    // Filter staff with valid IDs and transform to hierarchy type
    const validStaff: StaffMemberWithHierarchy[] = staffMembers
      .filter(s => s.id)
      .map(staff => ({
        id: staff.id!,
        first_name: staff.first_name,
        last_name: staff.last_name,
        email: staff.email,
        position: staff.position,
        department: staff.department || 'Unknown',
        photo_url: staff.photo_url,
        manager_id: undefined, // TODO: Add manager_id to database
        reports: []
      }));

    // First pass: create map
    validStaff.forEach(staff => {
      staffMap.set(staff.id, staff);
    });

    // Second pass: build hierarchy
    validStaff.forEach(staff => {
      const staffNode = staffMap.get(staff.id);
      if (!staffNode) return;

      if (staff.manager_id && staffMap.has(staff.manager_id)) {
        const manager = staffMap.get(staff.manager_id);
        if (manager) {
          manager.reports = manager.reports || [];
          manager.reports.push(staffNode);
        }
      } else {
        // No manager = top level
        rootStaff.push(staffNode);
      }
    });

    setOrgStructure(rootStaff);
  };

  const departments = Array.from(new Set(staffMembers.map(s => s.department).filter(Boolean))) as string[];

  // Transform and filter staff for display
  const filteredStaff: StaffMemberWithHierarchy[] = staffMembers
    .filter(staff => staff.id) // Filter out staff without IDs
    .filter(staff => {
      const matchesSearch = searchQuery === '' || 
        staff.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        staff.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        staff.position.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesDepartment = selectedDepartment === 'all' || staff.department === selectedDepartment;
      
      return matchesSearch && matchesDepartment;
    })
    .map(staff => ({
      id: staff.id!,
      first_name: staff.first_name,
      last_name: staff.last_name,
      email: staff.email,
      position: staff.position,
      department: staff.department || 'Unknown',
      photo_url: staff.photo_url,
      manager_id: undefined,
      reports: []
    }));

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getDepartmentColor = (department: string) => {
    const colors: Record<string, string> = {
      'Engineering': 'bg-blue-500',
      'Sales': 'bg-green-500',
      'Marketing': 'bg-purple-500',
      'Finance': 'bg-yellow-500',
      'HR': 'bg-pink-500',
      'Operations': 'bg-orange-500',
      'Customer Success': 'bg-indigo-500',
    };
    return colors[department] || 'bg-gray-500';
  };

  const StaffCard: React.FC<{ staff: StaffMemberWithHierarchy; isCompact?: boolean }> = ({ staff, isCompact = false }) => (
    <div className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow ${isCompact ? 'p-3' : 'p-4'}`}>
      <div className="flex items-center gap-3">
        <div className="relative">
          {staff.photo_url ? (
            <img src={staff.photo_url} alt={`${staff.first_name} ${staff.last_name}`} 
              className={`rounded-full object-cover ${isCompact ? 'w-10 h-10' : 'w-12 h-12'}`} />
          ) : (
            <div className={`${isCompact ? 'w-10 h-10 text-sm' : 'w-12 h-12 text-base'} rounded-full ${getDepartmentColor(staff.department)} flex items-center justify-center text-white font-bold`}>
              {getInitials(staff.first_name, staff.last_name)}
            </div>
          )}
          {staff.reports && staff.reports.length > 0 && (
            <div className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {staff.reports.length}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-gray-900 truncate ${isCompact ? 'text-sm' : 'text-base'}`}>
            {staff.first_name} {staff.last_name}
          </h4>
          <p className={`text-gray-600 truncate ${isCompact ? 'text-xs' : 'text-sm'}`}>
            {staff.position}
          </p>
          {!isCompact && (
            <p className="text-xs text-gray-500 truncate">{staff.department}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <a href={`mailto:${staff.email}`} className="text-blue-600 hover:text-blue-700" title="Email">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );

  const HierarchyNode: React.FC<{ staff: StaffMemberWithHierarchy; level?: number }> = ({ staff, level = 0 }) => (
    <div className="relative">
      <div className={`${level > 0 ? 'ml-8' : ''}`}>
        {level > 0 && (
          <div className="absolute left-0 top-6 w-8 h-px bg-gray-300"></div>
        )}
        <div className="mb-4">
          <StaffCard staff={staff} isCompact={level > 1} />
        </div>
        {staff.reports && staff.reports.length > 0 && (
          <div className="relative pl-4 border-l-2 border-gray-300">
            {staff.reports.map((report: StaffMemberWithHierarchy, index: number) => (
              <HierarchyNode key={report.id} staff={report} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const DepartmentView: React.FC = () => {
    const departmentGroups = departments.map(dept => ({
      name: dept,
      staff: filteredStaff.filter(s => s.department === dept)
    })).filter(g => g.staff.length > 0);

    return (
      <div className="space-y-6">
        {departmentGroups.map(group => (
          <div key={group.name} className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getDepartmentColor(group.name)}`}></div>
                {group.name}
              </h3>
              <span className="text-sm text-gray-600">{group.staff.length} members</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {group.staff.map(staff => (
                <StaffCard key={staff.id} staff={staff} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ListView: React.FC = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Position</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Department</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Reports</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {filteredStaff.map(staff => (
            <tr key={staff.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  {staff.photo_url ? (
                    <img src={staff.photo_url} alt={`${staff.first_name} ${staff.last_name}`} 
                      className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className={`w-8 h-8 rounded-full ${getDepartmentColor(staff.department)} flex items-center justify-center text-white text-xs font-bold`}>
                      {getInitials(staff.first_name, staff.last_name)}
                    </div>
                  )}
                  <span className="font-medium text-gray-900">{staff.first_name} {staff.last_name}</span>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">{staff.position}</td>
              <td className="px-6 py-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getDepartmentColor(staff.department)}`}>
                  {staff.department}
                </span>
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">{staff.email}</td>
              <td className="px-6 py-4 text-sm text-gray-600">
                {staff.reports?.length || 0} direct reports
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold text-gray-800">Organization Chart</h3>
          <p className="text-gray-600 mt-1">{staffMembers.length} team members</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => toast('PDF export coming soon!', { icon: '📄' })}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
          >
            Export PDF
          </button>
          <button
            onClick={() => toast('Print view coming soon!', { icon: '🖨️' })}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition text-sm"
          >
            Print
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name, position..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Department Filter */}
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>

          {/* View Type */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewType('hierarchy')}
              className={`px-4 py-2 rounded-md transition ${
                viewType === 'hierarchy' 
                  ? 'bg-white shadow text-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Hierarchy View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewType('department')}
              className={`px-4 py-2 rounded-md transition ${
                viewType === 'department' 
                  ? 'bg-white shadow text-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Department View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </button>
            <button
              onClick={() => setViewType('list')}
              className={`px-4 py-2 rounded-md transition ${
                viewType === 'list' 
                  ? 'bg-white shadow text-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="List View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-600">Total Staff</p>
          <p className="text-2xl font-bold text-gray-900">{filteredStaff.length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-600">Departments</p>
          <p className="text-2xl font-bold text-gray-900">{departments.length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-600">Managers</p>
          <p className="text-2xl font-bold text-gray-900">
            {(() => {
              const countManagers = (staff: StaffMemberWithHierarchy[]): number => {
                return staff.reduce((count, s) => count + (s.reports && s.reports.length > 0 ? 1 : 0), 0);
              };
              return countManagers(orgStructure);
            })()}
          </p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-600">Avg Team Size</p>
          <p className="text-2xl font-bold text-gray-900">
            {(() => {
              const countManagers = (staff: StaffMemberWithHierarchy[]): number => {
                return staff.reduce((count, s) => count + (s.reports && s.reports.length > 0 ? 1 : 0), 0);
              };
              const managerCount = countManagers(orgStructure);
              return managerCount > 0 ? Math.round(staffMembers.length / managerCount) : 0;
            })()}
          </p>
        </div>
      </div>

      {/* Content */}
      <div>
        {viewType === 'hierarchy' && (
          <div className="space-y-4">
            {orgStructure.length > 0 ? (
              orgStructure.map(staff => (
                <HierarchyNode key={staff.id} staff={staff} />
              ))
            ) : (
              <div className="bg-white rounded-xl p-12 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-gray-600">No staff members found matching your search</p>
              </div>
            )}
          </div>
        )}

        {viewType === 'department' && <DepartmentView />}

        {viewType === 'list' && <ListView />}
      </div>
    </div>
  );
};

export default OrgChart;
